/**
 * Fias Roles Management — the role catalogue.
 *
 * Route-backed rather than local-state tabs, matching the Providers section:
 * ops URLs get pasted into incident threads, so each view must be directly
 * addressable.
 */

import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ShieldAlert, ShieldCheck, Shield, Users, Lock, AlertTriangle } from 'lucide-react';
import { api, type AdminRoleSummary } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

/**
 * Sensitivity is conveyed by icon + text as well as colour. A colour-only
 * signal is invisible to a colour-blind operator, and this is the surface where
 * misreading "critical" as "routine" matters most.
 */
const SENSITIVITY: Record<
  AdminRoleSummary['sensitivity'],
  { icon: typeof Shield; label: string; className: string }
> = {
  critical: { icon: ShieldAlert, label: 'Critical', className: 'text-destructive' },
  elevated: {
    icon: ShieldCheck,
    label: 'Elevated',
    className: 'text-amber-600 dark:text-amber-500',
  },
  routine: { icon: Shield, label: 'Routine', className: 'text-muted-foreground' },
};

function RoleCard({ role }: { role: AdminRoleSummary }) {
  const sensitivity = SENSITIVITY[role.sensitivity];
  const Icon = sensitivity.icon;

  return (
    <Link to={`/dev-console/roles/${role.role}`} className="block">
      <Card className="h-full transition-colors hover:border-primary/50">
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <CardTitle className="text-base">{role.label}</CardTitle>
            <span
              className={`flex shrink-0 items-center gap-1 text-xs font-medium ${sensitivity.className}`}
            >
              <Icon className="h-3.5 w-3.5" aria-hidden="true" />
              {sensitivity.label}
            </span>
          </div>
          <CardDescription>{role.description}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-2">
            <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <Users className="h-4 w-4" aria-hidden="true" />
              {role.memberCount === null
                ? 'Members unknown'
                : `${role.memberCount} ${role.memberCount === 1 ? 'member' : 'members'}`}
            </span>

            {role.readOnly && (
              <Badge variant="outline" className="gap-1">
                <Lock className="h-3 w-3" aria-hidden="true" />
                Read-only
              </Badge>
            )}

            {/*
              Without this an operator would reasonably assume a listed member
              can do something. Nothing reads these roles yet.
            */}
            {role.reserved && (
              <Badge variant="secondary" title="No code path reads this role yet">
                Reserved — not enforced
              </Badge>
            )}

            <code className="ml-auto text-xs text-muted-foreground">{role.role}</code>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

export function RolesPage() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: queryKeys.adminRoles.catalogue(),
    queryFn: () => api.adminGetRoles(),
  });

  return (
    <div className="container mx-auto max-w-5xl px-4 py-8">
      <Link
        to="/dev-console"
        className="mb-6 inline-block text-sm text-muted-foreground hover:text-foreground"
      >
        ← Back to Dev Console
      </Link>

      <h1 className="text-2xl font-semibold text-foreground">Fias Roles</h1>
      <p className="mt-1 mb-6 max-w-2xl text-sm text-muted-foreground">
        Global platform roles and who holds them. Review this list periodically — an unexpected name
        on a critical role is the clearest signal that something is wrong.
      </p>

      {isLoading && <p className="text-sm text-muted-foreground">Loading roles…</p>}

      {isError && (
        <p className="text-sm text-destructive">
          {error instanceof Error ? error.message : 'Failed to load roles.'}
        </p>
      )}

      {data && !data.indexAvailable && (
        <p className="mb-6 flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
          <AlertTriangle
            className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-500"
            aria-hidden="true"
          />
          <span>
            The role membership index is not available yet, so member counts cannot be shown. Roles
            themselves are unaffected — this list is incomplete, not empty. Deploy the RoleIndex GSI
            and run the backfill to populate it.
          </span>
        </p>
      )}

      {data && (
        <div className="grid gap-4 sm:grid-cols-2">
          {data.roles.map((role) => (
            <RoleCard key={role.role} role={role} />
          ))}
        </div>
      )}
    </div>
  );
}
